import React from 'react';
import { Modal as AntModal } from 'antd';
import type { ModalProps as AntModalProps } from 'antd';

import { withAuthGuard } from '../../auth';
import type { AuthProps } from '../../auth';

export type ModalProps = AntModalProps &
    AuthProps & {
        /**
         * EAA-2061: bound the dialog to the visible screen — header and footer stay put
         * and the caller's content (a `flex: 1; min-height: 0` child that scrolls) is the
         * one scroll region. Its wrap never scrolls, so the dialog can never slide off the
         * top of the screen. Implies `centered`; the caller's `styles` still win per key.
         */
        fitViewport?: boolean;
    };

type SemanticStyles = Exclude<AntModalProps['styles'], ((...args: never[]) => unknown) | undefined>;

/**
 * WHY a non-scrolling wrap: a centred antd dialog sits in a full-screen wrap that is
 * itself `overflow: auto`. With the shell capped near the screen height, the wrap still
 * ended up taller than the screen in the browser (the question-type dialogs, EAA-2061:
 * a wrap scrollbar about a third of the screen tall), and scrolling it slid the title
 * and fields off the top. The wrap's own line box is zeroed (the dialog resets font
 * size and line height for its content) and it cannot scroll; `100dvh` is the visible
 * height, not the layout height a mobile toolbar or zoom can make larger.
 */
export const FIT_VIEWPORT_STYLES: SemanticStyles = {
    wrapper: { overflow: 'hidden', fontSize: 0, lineHeight: 0 },
    container: { display: 'flex', flexDirection: 'column', maxHeight: 'calc(100dvh - 48px)' },
    body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
};

function mergeStyles(base: SemanticStyles, own: SemanticStyles | undefined): SemanticStyles {
    if (!own) return base;
    const out: Record<string, React.CSSProperties | undefined> = { ...base };
    for (const [slot, value] of Object.entries(own)) {
        out[slot] = {
            ...(base as Record<string, React.CSSProperties | undefined>)[slot],
            ...value,
        };
    }
    return out as SemanticStyles;
}

const BaseModal = React.forwardRef<unknown, AntModalProps & { fitViewport?: boolean }>(
    ({ fitViewport, styles, centered, ...rest }, ref) => {
        if (!fitViewport) {
            return <AntModal {...rest} {...{ ref }} styles={styles} centered={centered} />;
        }
        const merged =
            typeof styles === 'function'
                ? (info: Parameters<typeof styles>[0]) =>
                      mergeStyles(FIT_VIEWPORT_STYLES, styles(info) as SemanticStyles)
                : mergeStyles(FIT_VIEWPORT_STYLES, styles as SemanticStyles | undefined);
        return (
            <AntModal {...rest} {...{ ref }} centered styles={merged as AntModalProps['styles']} />
        );
    }
);
BaseModal.displayName = 'Modal';

const GuardedModal = withAuthGuard(BaseModal, {
    defaultFallback: 'hide',
}) as unknown as React.ForwardRefExoticComponent<ModalProps & React.RefAttributes<HTMLDivElement>>;

/**
 * Themed, auth-gated pass-through of antd's `Modal`. Static helpers
 * (`confirm`/`info`/`success`/`error`/`warning`/`useModal`) are copied
 * through unchanged — they're imperative APIs with no `auth` prop to gate.
 */
export const Modal = Object.assign(GuardedModal, {
    confirm: AntModal.confirm,
    info: AntModal.info,
    success: AntModal.success,
    error: AntModal.error,
    warning: AntModal.warning,
    useModal: AntModal.useModal,
});
